import {
  buildContext,
  buildRecommendationEngine,
  loadAdvancedSearchQueryActions,
  RecommendationEngine,
} from '@coveo/headless/recommendation';
import { GetCoveoConfig, GetCoveoToken, GetFreshCoveoToken } from './Helpers/CoveoApi';
import { ICoveoSearchConfig, PersonalizedFieldNames } from './Models/CoveoModels';
import { useRef, useState, useEffect, createContext, useContext } from 'react';
import { getCoveoFieldNameByString } from './Mappers/CoveoFieldMapper';
import { getDefaultUserStateFilter, getUserQueryFilters } from './Helpers/UserHelper';
import { IUserData } from 'Foundation/User/UserContextModels';

interface ICoveoRecommendationEngineProps {
  analyticsEnabled: boolean;
  children: any;
  //aq: string;
}

type CoveoRecommendationEngineContextType = {
  recommendationEngine: RecommendationEngine<{}> | null | undefined;
};

const CoveoRecommendationContext = createContext<any | undefined>(undefined);
CoveoRecommendationContext.displayName = 'CoveoRecommendationContext';

export function useCoveoRecommendationEngine(): CoveoRecommendationEngineContextType {
  const context = useContext(CoveoRecommendationContext);
  if (context === undefined) {
    console.error(`useCoveoRecommendationEngine must be used within a CoveoRecommendationEngineProvider`);
    return { recommendationEngine: null };
  }
  return context;
}

export function CoveoRecommendationEngineProvider(props: ICoveoRecommendationEngineProps) {
  const recommendationEngineContext = useRef<CoveoRecommendationEngineContextType | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    const getEngine = async () => {
      if (!recommendationEngineContext.current) {
        const engine = await CreateRecommendationEngine(props.analyticsEnabled);
        if (engine) {
          recommendationEngineContext.current = {
            recommendationEngine: engine,
          };
          buildContext(engine!);
        } else {
          console.error("Can't load coveo engine so all coveo components won't load");
        }
      }
      setLoaded(true);
    };

    getEngine();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return loaded ? (
    <CoveoRecommendationContext.Provider value={recommendationEngineContext.current}>
      {props.children}
    </CoveoRecommendationContext.Provider>
  ) : (
    <></>
  );
}

async function CreateRecommendationEngine(analyticsEnabled: boolean) {
  const searchConfig: ICoveoSearchConfig | null = await GetCoveoConfig();
  if (!searchConfig || !searchConfig.organizationId || searchConfig.organizationId === '') {
    console.error('Coveo config not found');
    return null;
  }
  try {
    let token = await GetCoveoToken();
    if (token === '') {
      console.error('Coveo token not found');
      return null;
    }
    var headlessRecommendationEngine = buildRecommendationEngine({
      configuration: {
        organizationId: searchConfig.organizationId,
        accessToken: token,
        renewAccessToken: GetFreshCoveoToken,
        pipeline: 'IAPortals Content Recommendation',
        searchHub: 'news_recommendations',
        analytics: {
          enabled: analyticsEnabled,
        },
      },
    });
    const advQuery = loadAdvancedSearchQueryActions(headlessRecommendationEngine);
    const action = advQuery.registerAdvancedSearchQueries({ aq: getRecommendationAQ(searchConfig) });
    headlessRecommendationEngine.dispatch(action);
    return headlessRecommendationEngine;
  } catch {
    return null;
  }
}

function getRecommendationAQ(searchConfig: ICoveoSearchConfig): string {
  console.log(getAQForSite(searchConfig, undefined));
  return '@z95xtemplatename == "news standard page"';
}

function getAQForSite(searchConfig: ICoveoSearchConfig, userData: IUserData | undefined): string {
  let aq =
    '((@' +
    getCoveoFieldNameByString(PersonalizedFieldNames.source)?.coveoFieldName +
    '=="' +
    searchConfig.searchSource +
    '"' +
    ' AND (@' +
    getCoveoFieldNameByString(PersonalizedFieldNames.site)?.coveoFieldName +
    '=="' +
    searchConfig.siteName +
    '"';
  aq += ')))';
  if (userData) {
    aq += getUserQueryFilters(userData);
    aq += getDefaultUserStateFilter(userData);
  }
  return aq;
}
